import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useCategoriesViewModel } from '../viewmodels/useCategoriesViewModel';
import { useProductsViewModel } from '../viewmodels/useProductsViewModel';
import ProductGrid from '../components/product/ProductGrid';

const CategoryPage: React.FC = () => {
  const { handle } = useParams<{ handle: string }>();
  const { getCategoryByHandle, isLoading: categoriesLoading } = useCategoriesViewModel();
  const {
    products,
    isLoading: productsLoading,
    error,
    updateFilters,
  } = useProductsViewModel();

  const category = handle ? getCategoryByHandle(handle) : undefined;

  // Filter products by category ID when category is resolved
  useEffect(() => {
    if (category?.id) {
      updateFilters({ category: category.id });
    }
  }, [category?.id, updateFilters]);

  const children = (category?.category_children || [])
    .filter((c) => c.is_active && !c.is_internal)
    .sort((a, b) => a.rank - b.rank);

  if (categoriesLoading) {
    return (
      <div className="container mx-auto px-lg py-3xl text-center" style={{ maxWidth: 'var(--container-max-width)' }}>
        <p className="text-text-secondary">Loading category...</p>
      </div>
    );
  }

  if (!category) {
    return (
      <div className="container mx-auto px-lg py-3xl text-center" style={{ maxWidth: 'var(--container-max-width)' }}>
        <h1 className="font-serif text-4xl font-semibold text-primary mb-md">Category Not Found</h1>
        <p className="text-text-secondary mb-xl">The category you're looking for doesn't exist.</p>
        <a
          href="/products"
          className="inline-block px-xl py-md bg-primary text-[var(--color-text-on-primary)] no-underline rounded-sm text-sm font-medium hover:bg-primary-hover transition-colors"
        >
          Browse All Products
        </a>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-lg py-3xl" style={{ maxWidth: 'var(--container-max-width)' }}>
      {/* Category Header */}
      <div className="mb-3xl text-center">
        <h1 className="font-serif text-4xl md:text-5xl font-semibold text-primary mb-md">
          {category.name}
        </h1>
        {category.description && (
          <p className="text-text-secondary text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
            {category.description}
          </p>
        )}
      </div>

      {/* Child Category Chips */}
      {children.length > 0 && (
        <div className="flex flex-wrap gap-3 justify-center mb-2xl">
          {children.map((child) => (
            <a
              key={child.id}
              href={`/categories/${child.handle}`}
              className="px-4 py-2 border border-border rounded-full text-sm text-text-secondary no-underline hover:border-primary hover:text-primary transition-colors"
            >
              {child.name}
            </a>
          ))}
        </div>
      )}

      {/* Product Grid */}
      <ProductGrid
        products={products}
        isLoading={productsLoading}
        error={error}
      />
    </div>
  );
};

export default CategoryPage;
